import { FormControl, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { SelectItem } from 'primeng/api';

export interface AddressInfo {
  street?: string;
  zip?: string;
  city?: string;
  state?: string;
}

export const emptyAddress: Required<AddressInfo> = {
  street: '',
  zip: '',
  city: '',
  state: '',
};

export interface BorrowerInfo {
  firstName?: string;
  middleName?: string;
  lastName?: string;
  suffix?: string;
}

export const emptyBorrower: Required<BorrowerInfo> = {
  firstName: '',
  middleName: '',
  lastName: '',
  suffix: '',
};

export interface BorrowerSummary {
  fileDataId?: number;
  applicationId?: number;
  borrowerId?: number;
  borrowerInfo?: BorrowerInfo;
  borrowerPhone?: number;
  residenceAddress?: AddressInfo;
}

export interface ClientAddressInfo {
  name?: string;
  phone?: string;
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
}

export enum IrsForm4506FormType {
  NotAssigned = 'NotAssigned',
  Personal = 'Personal',
  Business = 'Business',
}

export enum IrsFilingStatus {
  NotAssigned = 'NotAssigned',
  Jointly = 'Jointly',
  NotJointly = 'NotJointly',
}

export const filingStatusOptions: SelectItem<IrsFilingStatus>[] = [
  { label: 'Select', value: IrsFilingStatus.NotAssigned },
  { label: 'Jointly', value: IrsFilingStatus.Jointly },
  { label: 'Not Jointly', value: IrsFilingStatus.NotJointly },
];

export enum TranscriptRequests {
  NotAssigned = 'NotAssigned',
  TaxTranscript = 'TaxTranscript',
  WageAndIncomeTranscript = 'WageAndIncomeTranscript',
}

export enum Transcript {
  NotAssigned = 'NotAssigned',
  Form1040 = '_1040',
  Form1065 = '_1065',
  Form1120 = '_1120',
  Form1120S = '_1120S',
  FormW2 = '_W2',
  Form1099 = '_1099',
}

export enum TranscriptType {
  NotAssigned = 'NotAssigned',
  Return = 'Return',
  Account = 'Account',
  RecordOfAccount = 'RecordOfAccount',
}

export const transcriptTypeOptions: SelectItem<TranscriptType>[] = [
  { label: 'Select', value: TranscriptType.NotAssigned },
  { label: 'Return Transcript', value: TranscriptType.Return },
  { label: 'Account Transcript', value: TranscriptType.Account },
  { label: 'Record of Account', value: TranscriptType.RecordOfAccount },
];

export enum ThirdPartyVendor {
  NotAssigned = 'NotAssigned',
  EquifaxWorkforceSolutions = 'EquifaxWorkforceSolutions',
  WholesaleCreditPlus = 'WholesaleCreditPlus',
  BankCMGXactus = 'BankCMGXactus',
}

export const thirdPartyVendorOptions: SelectItem<ThirdPartyVendor>[] = [
  { label: 'Select', value: ThirdPartyVendor.NotAssigned },
  { label: 'Equifax Workforce Solutions LLC', value: ThirdPartyVendor.EquifaxWorkforceSolutions },
  { label: 'Wholesale Credit Plus', value: ThirdPartyVendor.WholesaleCreditPlus },
];

/** Set of ThirdPartyVendor options to expose only for CMGBank */
export const cmgBankThirdPartyVendorOptions: SelectItem<ThirdPartyVendor>[] = [
  { label: 'Select', value: ThirdPartyVendor.NotAssigned },
  { label: 'Bank CMG - Xactus, LLC', value: ThirdPartyVendor.BankCMGXactus },
];

export enum IrsFormsStatus {
  NoForms = 'NoForms',
  Incomplete = 'Incomplete',
  Complete = 'Complete',
}

export interface IrsForm4506LoanModel {
  fileDataId?: number;
  borrowers?: BorrowerSummary[];
  irsForm4506s?: IrsForm4506[];
  irsFormsConfirmed?: boolean;
  irsFormsStatus?: IrsFormsStatus;
  isCMGBankBranchLoan?: boolean;
}

export interface UpdateIrsForm4506LoanModelRequest {
  fileDataId?: number;
  irsForm4506s?: IrsForm4506[];
  deletedIrsForm4506Ids?: number[];
  irsFormsConfirmed?: boolean;
}

export interface IrsForm4506 {
  irsFormComplete?: IrsFormsStatus;
  /**
   * Indicates whether form was saved by Clear 2. Used to identify forms that
   * were created automatically by Byte which does not follow all form load
   * requirements for 4506-C Feature in Clear 2. Forms where this is `false`
   * should be ignored and replaced by the UI with a new set of forms.
   */
  isClear2Form?: boolean;
  fileDataId?: number;
  irsForm4506Id?: number;
  borrowerId?: number;
  formType?: IrsForm4506FormType;
  filingStatus?: IrsFilingStatus;
  overrideCurrentName?: boolean;
  currentInfoOV?: BorrowerInfo;
  overrideReturnName?: boolean;
  returnInfoOV?: BorrowerInfo;
  overrideSpouse?: boolean;
  spouseInfoOV?: BorrowerInfo;
  overrideCurrentAddress?: boolean;
  currentAddressOV?: AddressInfo;
  /** UI-only field, calculated from `lastReturnAddress` */
  overrideLastReturnAddress?: boolean;
  lastReturnAddress?: AddressInfo;
  einOV?: string;
  title?: string;
  transcriptRequests?: TranscriptRequests;
  transcript?: Transcript;
  transcriptType?: TranscriptType;
  taxPeriodYearsRequested?: number[];
  thirdPartyVendor?: ThirdPartyVendor;
  phoneOV?: string;
  /** UI-only disabled field to render "false" */
  wageAndIncomeTranscriptDisabled: boolean;
  /** UI-only disabled field to render borrower info from `borrowerId` value */
  currentInfoDisabled?: BorrowerInfo;
  /** UI-only disabled field to render borrower address from `borrowerId` value */
  currentAddressDisabled?: AddressInfo;
  /** UI-only disabled field to render spouse info based on `borrowerId` / `filingStatus` value */
  spouseInfoDisabled?: BorrowerInfo;
}

export type SimpleFormGroup<T> = FormGroup<{ [K in keyof Required<T>]: FormControl<Required<T>[K]> }>;
export type ModelFormGroup<T> = FormGroup<{
  [K in keyof Required<T>]: Required<T>[K] extends any[]
    ? FormControl<Required<T>[K]>
    : Required<T>[K] extends object
      ? SimpleFormGroup<Required<T>[K]>
      : FormControl<Required<T>[K]>;
}>;

export const validateForm4506C: ValidatorFn = (group: ModelFormGroup<IrsForm4506>): ValidationErrors | null => {
  const value = group.getRawValue();
  const required = { required: true };
  if (!value.borrowerId) {
    group.controls.borrowerId.setErrors(required);
  }

  if (!value.formType || value.formType === IrsForm4506FormType.NotAssigned) {
    group.controls.formType.setErrors(required);
  }

  if (value.formType === IrsForm4506FormType.Personal) {
    if (!value.filingStatus || value.filingStatus === IrsFilingStatus.NotAssigned) {
      group.controls.filingStatus.setErrors(required);
    }

    if (value.overrideCurrentName) {
      if (!value.currentInfoOV.firstName) {
        group.controls.currentInfoOV.controls.firstName.setErrors(required);
      }

      if (!value.currentInfoOV.lastName) {
        group.controls.currentInfoOV.controls.lastName.setErrors(required);
      }
    } else {
      if (!value.currentInfoDisabled.firstName) {
        group.controls.currentInfoOV.controls.firstName.setErrors(required);
      }

      if (!value.currentInfoDisabled.lastName) {
        group.controls.currentInfoOV.controls.lastName.setErrors(required);
      }
    }
  } else if (value.formType === IrsForm4506FormType.Business) {
    if (!value.currentInfoOV.lastName) {
      group.controls.currentInfoOV.controls.lastName.setErrors(required);
    }

    if (!value.einOV) {
      group.controls.einOV.setErrors(required);
    }

    if (!value.title) {
      group.controls.title.setErrors(required);
    }
  }

  if (value.overrideCurrentAddress) {
    if (!value.currentAddressOV.street) {
      group.controls.currentAddressOV.controls.street.setErrors(required);
    }

    if (!value.currentAddressOV.zip) {
      group.controls.currentAddressOV.controls.zip.setErrors(required);
    }

    if (!value.currentAddressOV.city) {
      group.controls.currentAddressOV.controls.city.setErrors(required);
    }

    if (!value.currentAddressOV.state) {
      group.controls.currentAddressOV.controls.state.setErrors(required);
    }
  } else {
    if (!value.currentAddressDisabled.street) {
      group.controls.currentAddressOV.controls.street.setErrors(required);
    }

    if (!value.currentAddressDisabled.zip) {
      group.controls.currentAddressOV.controls.zip.setErrors(required);
    }

    if (!value.currentAddressDisabled.city) {
      group.controls.currentAddressOV.controls.city.setErrors(required);
    }

    if (!value.currentAddressDisabled.state) {
      group.controls.currentAddressOV.controls.state.setErrors(required);
    }
  }

  if (!value.transcriptRequests || value.transcriptRequests === TranscriptRequests.NotAssigned) {
    group.controls.transcriptRequests.setErrors(required);
  }

  if (!value.transcript || value.transcript === Transcript.NotAssigned) {
    group.controls.transcript.setErrors(required);
  }

  if (
    value.transcriptRequests === TranscriptRequests.TaxTranscript &&
    (!value.transcriptType || value.transcriptType === TranscriptType.NotAssigned)
  ) {
    group.controls.transcriptType.setErrors(required);
  }

  if (!value.taxPeriodYearsRequested?.length) {
    group.controls.taxPeriodYearsRequested.setErrors(required);
  }

  if (!value.thirdPartyVendor || value.thirdPartyVendor === ThirdPartyVendor.NotAssigned) {
    group.controls.thirdPartyVendor.setErrors(required);
  }

  return null;
};
